/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import TransactionalMessageModel, { TransactionalMessageChannelType } from 'cio/models/transactional-message';
import srcDoc from 'srcdoc-polyfill';
import { inject as service } from '@ember/service';
import PushService from 'cio/services/push';
import { Platform } from 'cio/models/action';
import { getPushPreviewMeta } from 'cio/utils/push/preview';
import { Editor } from 'cio/services/editor';

interface BaseTransactionalContentPreviewArgs {
  message: TransactionalMessageModel;
  compact: boolean;
  hasRevealable: boolean;
  readOnly: boolean;
  buttonOrLink: 'button' | 'link';
}

interface ButtonTransactionalContentPreviewArgs extends BaseTransactionalContentPreviewArgs {
  buttonOrLink: 'button';
  onEdit: () => unknown;
}

interface LinkTransactionalContentPreviewArgs extends BaseTransactionalContentPreviewArgs {
  buttonOrLink: 'link';
  route: string;
  model: string;
}

type TransactionalContentPreviewArgs = ButtonTransactionalContentPreviewArgs | LinkTransactionalContentPreviewArgs;

type PlatformMeta = {
  key: string;
};

export default class TransactionalContentPreview extends Component<TransactionalContentPreviewArgs> {
  @service push!: PushService;

  constructor(owner: unknown, args: TransactionalContentPreviewArgs) {
    super(owner, args);

    if (this.args.buttonOrLink === 'button' && this.args.onEdit == null) {
      throw new Error('onEdit is required when buttonOrLink is button');
    }

    if (this.args.buttonOrLink === 'link' && this.args.route == null) {
      throw new Error('route is required when buttonOrLink is link');
    }

    if (this.args.buttonOrLink === 'link' && this.args.model == null) {
      throw new Error('model is required when buttonOrLink is link');
    }
  }

  get pushMessage() {
    let { template } = this.args.message;
    if (template == null) {
      return null;
    }
    return getPushPreviewMeta(template, this.providers);
  }

  get pushPreviewDevice() {
    let platform = { platform: Platform.Android, type: 'heads-up' };

    // prefer iOS if both are enabled
    if (this.push.platforms.some((p: PlatformMeta) => p.key === Platform.iOS) === true) {
      platform = { platform: Platform.iOS, type: 'banner' };
    }

    return platform;
  }

  get providers() {
    let iosProvider = this.push.platforms.find((p: PlatformMeta) => p.key === 'ios')?.provider;
    let androidProvider = this.push.platforms.find((p: PlatformMeta) => p.key === 'android')?.provider;

    return { iosProvider, androidProvider };
  }

  get isCustomPushPayload() {
    return this.args.message.template.editor === Editor.JSON || this.pushMessage?.has_json;
  }

  get buttonOrLink() {
    return this.args.buttonOrLink ?? 'button';
  }

  get isButton() {
    return this.buttonOrLink === 'button';
  }

  get isLink() {
    return this.buttonOrLink === 'link';
  }

  get hasRevealable() {
    return this.args.hasRevealable ?? false;
  }

  get isEmail() {
    return this.args.message.type === TransactionalMessageChannelType.Email;
  }

  get isPush() {
    return this.args.message.type === TransactionalMessageChannelType.Push;
  }

  @action
  setSrcDoc(element: HTMLIFrameElement) {
    srcDoc.set(element);
  }
}
